import moment from "moment";
import { usdFormat } from "../../../../utils";
import { OrderItemReportRow } from "./type";
import { Link } from "react-router-dom";
import { Badge } from "../../../../components/Badge";
import { ColDef } from "ag-grid-community";

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: OrderItemReportRow;
}) => JSX.Element;

const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: OrderItemReportRow }) => string
): CellRenderer => {
  return (params) => (
    <>
      {permission ? (
        <Link to={linkFunc(params)}>
          <Badge>{params.value}</Badge>
        </Link>
      ) : (
        <Badge>{params.value}</Badge>
      )}
    </>
  );
};

const generateColDefs = (): ColDef<OrderItemReportRow>[] => [
  {
    headerName: "Date",
    valueGetter: (p) => p.data?.transaction_time,
    pinned: "left",
    valueFormatter: (p) => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format("ddd, MMM DD, YYYY h:mm a") : "";
    },
  },
  {
    headerName: "Date_Raw",
    valueGetter: (p) => p.data?.transaction_time,
    hide: true,
  },
  {
    headerName: "Order ID",
    valueGetter: (p) => p.data?.order_id,
    cellRenderer: createCellRenderer(
      true,
      (p) =>
        `/orders/${p.data?.payment_ref_id}/${p.data?.order_id}`
    ),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Username",
    valueGetter: (p) => p.data?.username,
    filter: "agTextColumnFilter",
  },
  // === Item-level columns ===
  {
    headerName: "Item Name",
    valueGetter: (p) => p.data?.item_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item ID",
    valueGetter: (p) => p.data?.item_id,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Price",
    valueGetter: (p) => p.data?.item_price,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Category",
    valueGetter: (p) => p.data?.item_category,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Tax",
    valueGetter: (p) => p.data?.item_tax,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Tax Type",
    valueGetter: (p) => p.data?.item_tax_type,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Tax %",
    valueGetter: (p) => p.data?.item_tax_percentage,
    valueFormatter: (params) =>
      params.value != null ? `${params.value}%` : "",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Discount Applied",
    valueGetter: (p) => p.data?.item_discount_applied,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Token Value Charged",
    valueGetter: (p) => p.data?.item_token_value_charged,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Cash Balance Charged",
    valueGetter: (p) => p.data?.item_cash_balance_charged,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Promo Balance Charged",
    valueGetter: (p) => p.data?.item_promo_balance_charged,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Promo Discount Applied",
    valueGetter: (p) => p.data?.item_promo_discount_applied,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Modifiers Applied",
    valueGetter: (p) => p.data?.item_modifiers_applied,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Modifiers Additional Price",
    valueGetter: (p) => p.data?.item_modifiers_additional_price,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  // === Transaction-level columns ===
  {
    headerName: "Ronin Ref ID",
    valueGetter: (p) => p.data?.payment_ref_id,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Payment Processor Ref ID",
    valueGetter: (p) => p.data?.order_mx_ref_id,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Order Status",
    valueGetter: (p) => p.data?.order_status,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Payment Status",
    valueGetter: (p) => p.data?.payment_status,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Payment Type",
    valueGetter: (p) => p.data?.payment_type,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Transaction Amount",
    valueGetter: (p) => p.data?.transaction_amount,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Item Sub Total",
    valueGetter: (p) => p.data?.item_subtotal,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Tax",
    valueGetter: (p) => p.data?.tax,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Tip",
    valueGetter: (p) => p.data?.tip,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Service Fee",
    valueGetter: (p) => p.data?.digital_surcharge,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Cashless Asset ID",
    valueGetter: (p) => p.data?.uid,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Customer Email",
    valueGetter: (p) => p.data?.email,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Last 4 of CC",
    valueGetter: (p) => p.data?.last4,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Card Type",
    valueGetter: (p) => p.data?.card_type,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Organization Name",
    valueGetter: (p) => p.data?.organization_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Event Name",
    valueGetter: (p) => p.data?.event_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Vendor Name",
    valueGetter: (p) => p.data?.vendor_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Location Name",
    valueGetter: (p) => p.data?.location_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Is Tab?",
    valueGetter: (p) => p.data?.is_tab,
    valueFormatter: (params) => (params.value ? "Yes" : "No"),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Tab Name",
    valueGetter: (p) => p.data?.tab_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Tab Nickname",
    valueGetter: (p) => p.data?.tab_nickname,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Applied Discount Name",
    valueGetter: (p) => p.data?.applied_discount_name,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Applied Discount Amount",
    valueGetter: (p) => p.data?.applied_discount_amount,
    valueFormatter: (params) => usdFormat.format((params.value ?? 0) / 100),
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Order Notes",
    valueGetter: (p) => p.data?.order_notes,
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Server Created Time",
    valueGetter: (p) => p.data?.created_at,
    valueFormatter: (p) => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
    },
    filter: "agTextColumnFilter",
  },
];

export const colDefs: ColDef<OrderItemReportRow>[] = generateColDefs();
